import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowBack, ErrorOutline, FilterAlt, FilterAltOutlined, HourglassEmpty, InfoOutlined, PersonRemoveOutlined, ReportProblemOutlined, WarningAmberRounded } from "@mui/icons-material";
import { Box, Chip, Skeleton, Tab, Tabs, Tooltip } from "@mui/material";
import { useGetOrgByIdQuery } from "state/rtkQuery/org/orgQueries";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { useGetUsersByOrgAndStatusQuery } from "state/rtkQuery/user/userQueries";
import { EMAIL_COLUMN_CHARACTER_TRUNCATE_LIMIT_APPROVER_VIEW } from "utils/constants";
import { useDebounce } from "utils/useDebounce";

import AvatarRandomColor from "../OrgHistory/components/ReusableDataGrid/AvatarRandomColor";
import ReusableDataGrid from "../OrgHistory/components/ReusableDataGrid/ReusableDataGrid";

// ─── Summary Badge ────────────────────────────────────────────────────────────

const SummaryBadge = ({ count, label, bg, border, textColor, icon }) => (
  <div
    style={{
      padding: "16px 28px",
      borderRadius: "12px",
      background: bg,
      border: `1px solid ${border}`,
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      minWidth: "140px",
      gap: "4px",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      {icon && (
        <span style={{ color: textColor, opacity: 0.7, lineHeight: 1, display: "flex" }}>
          {React.cloneElement(icon, { style: { fontSize: 28 } })}
        </span>
      )}
      <span style={{ fontSize: "30px", fontWeight: 700, color: textColor, lineHeight: 1 }}>
        {count ?? "—"}
      </span>
    </div>
    <span style={{ fontSize: "12px", color: textColor, fontWeight: 500 }}>
      {label}
    </span>
  </div>
);

// ─── Stuck check (request has been pending > 1 day) ─────────────────────────

export function isStuck(rawDate) {
  if (!rawDate) return false;
  const d = new Date(rawDate);
  if (isNaN(d.getTime())) return false;
  return Date.now() - d.getTime() > 24 * 60 * 60 * 1000;
}

export function getStuckDuration(rawDate) {
  if (!rawDate) return { short: "", long: "" };

  const d = new Date(rawDate);
  if (isNaN(d.getTime())) return { short: "", long: "" };

  const diffMs = Math.max(0, Date.now() - d.getTime());
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;
  const monthMs = 30 * dayMs;
  const yearMs = 365 * dayMs;

  if (diffMs < hourMs) {
    const value = Math.max(1, Math.floor(diffMs / minuteMs));
    return {
      short: `${value}m`,
      long: `${value} minute${value === 1 ? "" : "s"}`,
    };
  }

  if (diffMs < dayMs) {
    const value = Math.floor(diffMs / hourMs);
    return {
      short: `${value}h`,
      long: `${value} hour${value === 1 ? "" : "s"}`,
    };
  }

  if (diffMs < monthMs) {
    const value = Math.floor(diffMs / dayMs);
    return {
      short: `${value}d`,
      long: `${value} day${value === 1 ? "" : "s"}`,
    };
  }

  if (diffMs < yearMs) {
    const value = Math.floor(diffMs / monthMs);
    return {
      short: `${value}mo`,
      long: `${value} month${value === 1 ? "" : "s"}`,
    };
  }

  const value = Math.floor(diffMs / yearMs);
  return {
    short: `${value}y`,
    long: `${value} year${value === 1 ? "" : "s"}`,
  };
}

// ─── Shared name + email cell renderer ───────────────────────────────────────

export const NameCell = (params) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      flexDirection: "row",
      paddingLeft: "30px",
    }}
  >
    <AvatarRandomColor
      firstName={params.row.firstName}
      lastName={params.row.lastName}
      color={params.row.color}
    />
    <div style={{ display: "flex", flexDirection: "column", marginLeft: "20px" }}>
      <Tooltip
        title={<span style={{ fontSize: "12px" }}>{params.row.name}</span>}
        placement="top-start"
        arrow
      >
        <span
          className="table-history-userEmailJoined-cell"
          style={{ fontWeight: "600", fontSize: "14px" }}
        >
          {params.row.name}
        </span>
      </Tooltip>
      <Tooltip
        title={<span style={{ fontSize: "12px" }}>{params.row.email}</span>}
        arrow
        placement="bottom-start"
      >
        {params.row.email?.length > EMAIL_COLUMN_CHARACTER_TRUNCATE_LIMIT_APPROVER_VIEW ? (
          <div>
            <span
              className="table-cell-trucate-reason"
              style={{ paddingLeft: "0", fontSize: "13px" }}
            >
              {params.row.email?.slice(0, EMAIL_COLUMN_CHARACTER_TRUNCATE_LIMIT_APPROVER_VIEW) + "..."}
            </span>
          </div>
        ) : (
          <span style={{ fontSize: "13px" }}>{params.row.email}</span>
        )}
      </Tooltip>
    </div>
  </div>
);

// ─── Build columns (same pattern as RequestedTab / RemoveRequestedTab) ────────

export function buildColumns(rows, currentPage, total, changedByHeader, tabStatus) {
  return [
    {
      field: "name",
      headerName: `Showing ${(currentPage - 1) * 25 + 1} - ${(currentPage - 1) * 25 + (rows?.length || 0)
        } of ${total || 0} Users`,
      headerClassName: "history-tab-totalCount-header-approver-view",
      headerAlign: "left",
      align: "left",
      width: 350,
      renderCell: NameCell,
    },
    {
      field: "rawDate",
      headerName: "Most Recent Timestamp",
      headerClassName: "date-joined-table-header",
      headerAlign: "center",
      align: "center",
      width: 180,
      valueGetter: (params) => new Date(params.row.rawDate),
      renderCell: (params) => {
        const d = new Date(params.row.rawDate);
        const formatted = isNaN(d.getTime())
          ? "—"
          : d.toLocaleString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          });
        return <span className="history-tab-joinedDate-cell">{formatted}</span>;
      },
    },
    {
      field: "changedBy",
      headerName: changedByHeader,
      headerClassName: "orgName-column-header",
      headerAlign: "center",
      align: "center",
      width: 350,
      renderCell: (params) => (
        <Tooltip title={<p style={{ fontSize: "12px" }}>{params.row.changedBy}</p>}>
          <span className="table-cell-history">{params.row.changedBy}</span>
        </Tooltip>
      ),
    },
    {
      field: "dataIssues",
      headerName: "Data Issues",
      headerAlign: "center",
      align: "center",
      width: 120,
      sortable: false,
      renderHeader: () => {
        const shouldCheckEndDate = tabStatus !== "Remove-Requested";
        const headerTooltip = shouldCheckEndDate
          ? "Checks whether username and end_date are present."
          : "Checks whether username is present.";

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Tooltip title={<span style={{ fontSize: "12px" }}>{headerTooltip}</span>} arrow>
              <InfoOutlined style={{ fontSize: 14, color: "#6e7781", cursor: "help" }} />
            </Tooltip>
            <span>Data Issues</span>
          </div>
        );
      },
      renderCell: (params) => {
        const shouldCheckEndDate = tabStatus !== "Remove-Requested";
        const missingUserName = !params.row.hasUserName;
        const missingEndDate = shouldCheckEndDate && !params.row.hasEndDate;
        const bothMissing = missingUserName && missingEndDate;
        const noIssues = !missingUserName && !missingEndDate;

        if (noIssues) {
          return (
            <span style={{ fontSize: "12px", color: "#2e7d32" }}>
              No issues
            </span>
          );
        }

        const lines = [];
        if (missingUserName) lines.push("• username is missing");
        if (shouldCheckEndDate && missingEndDate) lines.push("• end_date is missing");

        const tooltipContent = (
          <div style={{ fontSize: "12px", lineHeight: 1.6 }}>
            {lines.map((l, i) => <div key={i}>{l}</div>)}
          </div>
        );

        if (bothMissing) {
          return (
            <Tooltip title={tooltipContent} arrow>
              <span style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "default" }}>
                <ErrorOutline style={{ fontSize: 18, color: "#d32f2f" }} />
                <span style={{ fontSize: "11px", color: "#d32f2f", fontWeight: 600 }}>Both</span>
              </span>
            </Tooltip>
          );
        }

        return (
          <Tooltip title={tooltipContent} arrow>
            <span style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "default" }}>
              <ReportProblemOutlined style={{ fontSize: 18, color: "#f59e0b" }} />
              <span style={{ fontSize: "11px", color: "#b45309", fontWeight: 600 }}>
                {missingUserName ? "Username" : "End date"}
              </span>
            </span>
          </Tooltip>
        );
      },
    },
    {
      headerName: "Request Status",
      headerAlign: "center",
      align: "center",
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const stuck = isStuck(params.row.rawDate);
        const duration = getStuckDuration(params.row.rawDate);

        return stuck ? (
          <Tooltip
            title={
              <span style={{ fontSize: "12px" }}>
                This request has been pending for more than 1 day and may be stuck.
                {duration.long ? ` Stuck for ${duration.long}.` : ""}
              </span>
            }
            arrow
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                color: "#b45309",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "default",
              }}
            >
              <WarningAmberRounded style={{ fontSize: 18, color: "#f59e0b" }} />
              {duration.short ? `Stuck ${duration.short}` : "Stuck"}
            </span>
          </Tooltip>
        ) : (
          <span style={{ fontSize: "12px", color: "#2e7d32" }}>Active</span>
        );
      },
    },
  ];
}

// ─── Stuck filter toggle button ───────────────────────────────────────────────

const StuckFilterToggle = ({ active, onToggle, stuckCount }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
    <Chip
      icon={
        active
          ? <FilterAlt style={{ fontSize: 15 }} />
          : <FilterAltOutlined style={{ fontSize: 15 }} />
      }
      label={active ? "Showing stuck only" : "Show stuck only"}
      onClick={onToggle}
      size="small"
      variant={active ? "filled" : "outlined"}
      sx={{
        borderColor: active ? "#f59e0b" : "#d0d7de",
        background: active ? "#fff8e1 !important" : "transparent",
        color: active ? "#b45309" : "#555",
        fontWeight: active ? 600 : 400,
        fontSize: "12px",
        "& .MuiChip-icon": { color: active ? "#b45309" : "#888" },
        "&:hover": { background: active ? "#fef3c7 !important" : "#f6f8fa !important" },
      }}
    />
    {stuckCount > 0 && (
      <span
        style={{
          fontSize: "12px",
          color: "#b45309",
          background: "#fff8e1",
          border: "1px solid #f9c74f",
          borderRadius: "12px",
          padding: "2px 10px",
          fontWeight: 600,
        }}
      >
        {stuckCount} stuck on this page
      </span>
    )}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const OpsOrgDetail = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // Requested tab state
  const [reqPage, setReqPage] = useState(1);
  const [reqSearch, setReqSearch] = useState("");
  const [reqStuckOnly, setReqStuckOnly] = useState(false);
  const debouncedReqSearch = useDebounce(reqSearch, 400);

  // Remove-Requested tab state
  const [removePage, setRemovePage] = useState(1);
  const [removeSearch, setRemoveSearch] = useState("");
  const [removeStuckOnly, setRemoveStuckOnly] = useState(false);
  const debouncedRemoveSearch = useDebounce(removeSearch, 400);

  // Falls back to the default (first) platform until the ECR Ops platform
  // selector (EcrOpsLayout) has resolved a selection.
  const { data: platformData, isFetching: isPlatformLoading } = useGetPlatformsQuery();
  const selectedPlatformId = useSelector((state) => state.ecrOpsPlatform?.selectedPlatformId);
  const platformId = selectedPlatformId || platformData?._id;

  const { data: orgData, isLoading: isOrgLoading } = useGetOrgByIdQuery(orgId);

  const { data: requestedData, isFetching: isRequestedLoading } =
    useGetUsersByOrgAndStatusQuery(
      {
        orgId,
        status: "Requested",
        platformId,
        page: reqPage,
        perPage: 25,
        email: debouncedReqSearch === "" ? undefined : debouncedReqSearch,
      },
      { skip: !platformId, refetchOnMountOrArgChange: true }
    );

  const { data: removeRequestedData, isFetching: isRemoveLoading } =
    useGetUsersByOrgAndStatusQuery(
      {
        orgId,
        status: "Remove-Requested",
        platformId,
        page: removePage,
        perPage: 25,
        email: debouncedRemoveSearch === "" ? undefined : debouncedRemoveSearch,
      },
      { skip: !platformId, refetchOnMountOrArgChange: true }
    );

  // Derive rows directly from query data instead of maintaining separate state
  const reqRows = requestedData?.users || [];
  const removeRows = removeRequestedData?.users || [];

  const filteredReqRows = reqStuckOnly ? reqRows.filter((r) => isStuck(r.rawDate)) : reqRows;
  const filteredRemoveRows = removeStuckOnly ? removeRows.filter((r) => isStuck(r.rawDate)) : removeRows;

  const reqStuckCount = reqRows.filter((r) => isStuck(r.rawDate)).length;
  const removeStuckCount = removeRows.filter((r) => isStuck(r.rawDate)).length;

  const reqColumns = buildColumns(
    filteredReqRows,
    reqPage,
    requestedData?.total,
    "Approved By",
    "Requested"
  );
  const removeColumns = buildColumns(
    filteredRemoveRows,
    removePage,
    removeRequestedData?.total,
    "Remove Requested By",
    "Remove-Requested"
  );

  return (
    <div style={{ background: "#f6f8fa", minHeight: "100vh" }}>
      <div style={{ maxWidth: "1300px", margin: "0 auto", padding: "2rem" }}>

        {/* Header card */}
        <div
          style={{
            background: "#fff",
            borderRadius: "12px",
            border: "1px solid #e1e4e8",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            padding: "16px 20px",
            marginBottom: "1.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
          }}
        >
          {/* Left: back + org info */}
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <button
              onClick={() => navigate("/ecr-ops/stuck-users")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                background: "none",
                border: "1px solid #d0d7de",
                borderRadius: "6px",
                padding: "7px 14px",
                cursor: "pointer",
                fontSize: "13px",
                color: "#444",
                flexShrink: 0,
              }}
            >
              <ArrowBack style={{ fontSize: 16 }} />
              Back
            </button>

            <div>
              <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: "#1a1a2e" }}>
                {isOrgLoading ? <Skeleton variant="text" width={220} height={30} /> : orgData?.name}
              </h2>
              <p style={{ margin: "4px 0 0", color: "#6e7781", fontSize: "13px" }}>
                Pending requests for this organization
              </p>
            </div>
          </div>

          {/* Right: summary badges */}
          <div style={{ display: "flex", gap: "10px", flexShrink: 0 }}>
            <SummaryBadge
              count={requestedData?.total}
              label="Requested"
              bg="#fff8e1"
              border="#f9c74f"
              textColor="#7d5200"
              icon={<HourglassEmpty />}
            />
            <SummaryBadge
              count={removeRequestedData?.total}
              label="Remove Requested"
              bg="#fff0f0"
              border="#e88"
              textColor="#8b0000"
              icon={<PersonRemoveOutlined />}
            />
          </div>
        </div>

        {/* Main content card */}
        <div
          style={{
            background: "#fff",
            borderRadius: "12px",
            border: "1px solid #e1e4e8",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}
        >
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{
                "& .MuiTab-root": { textTransform: "none", fontWeight: 500, fontSize: "14px" },
                "& .Mui-selected": { color: "#0b756c !important" },
                "& .MuiTabs-indicator": { backgroundColor: "#0b756c" },
              }}
            >
              <Tab label={`Requested (${requestedData?.total ?? 0})`} />
              <Tab label={`Remove Requested (${removeRequestedData?.total ?? 0})`} />
            </Tabs>
          </Box>

          {/* Tab panels */}
          <div style={{ padding: "20px 24px" }}>
            {activeTab === 0 && (
              <>
                <p style={{ margin: "0 0 12px", color: "#6e7781", fontSize: "13px", fontStyle: "italic" }}>
                  Users in Requested have been approved to join the organization and are currently in the Active Directory sync process.
                </p>
                <StuckFilterToggle
                  active={reqStuckOnly}
                  onToggle={() => setReqStuckOnly((v) => !v)}
                  stuckCount={reqStuckCount}
                />
                <ReusableDataGrid
                  columns={reqColumns}
                  rows={filteredReqRows}
                  orgName={orgData?.name}
                  viewType="Joined History"
                  setCurrentPage={setReqPage}
                  currentPage={reqPage}
                  totalRecords={requestedData?.total || 0}
                  isLoading={isRequestedLoading || isPlatformLoading}
                  setQuery={setReqSearch}
                  query={reqSearch}
                  checkboxSelection={false}
                />
              </>
            )}

            {activeTab === 1 && (
              <>
                <p style={{ margin: "0 0 12px", color: "#6e7781", fontSize: "13px", fontStyle: "italic" }}>
                  Users in Removals Requested have had a request made to revoke their access from the organization and are currently in the Active Directory sync process.
                </p>
                <StuckFilterToggle
                  active={removeStuckOnly}
                  onToggle={() => setRemoveStuckOnly((v) => !v)}
                  stuckCount={removeStuckCount}
                />
                <ReusableDataGrid
                  columns={removeColumns}
                  rows={filteredRemoveRows}
                  orgName={orgData?.name}
                  viewType="Removals Requested History"
                  setCurrentPage={setRemovePage}
                  currentPage={removePage}
                  totalRecords={removeRequestedData?.total || 0}
                  isLoading={isRemoveLoading || isPlatformLoading}
                  setQuery={setRemoveSearch}
                  query={removeSearch}
                  checkboxSelection={false}
                />
              </>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default OpsOrgDetail;
